import { PublicClientApplication } from "@azure/msal-browser";
import { invitationAuthOptions } from "./authRequest";

const tenantName = import.meta.env.VITE_ENTRA_TENANT_NAME || "";
const tenantId = import.meta.env.VITE_ENTRA_TENANT_ID || "";
const clientId = import.meta.env.VITE_ENTRA_CLIENT_ID || "";
const apiScope = import.meta.env.VITE_ENTRA_API_SCOPE || "";
const configured = Boolean(tenantName && tenantId && clientId && apiScope);

let application;
let initialization;
const returnPathKey = "oche-return-path";
const durableReturnPathKey = "oche-pending-auth-return";
const returnPathLifetimeMs = 2 * 60 * 60 * 1000;

const safeReturnPath = value => {
  if (!value || !globalThis.location) return "/";
  try {
    const url = new URL(value, globalThis.location.origin);
    if (url.origin !== globalThis.location.origin) return "/";
    return /^\/(claim|team-invite|register-team)\/[^/]+\/?$/i.test(url.pathname)
      ? `${url.pathname}${url.search}${url.hash}`
      : "/";
  } catch {
    return "/";
  }
};

const rememberReturnPath = value => {
  const path = safeReturnPath(value);
  sessionStorage.setItem(returnPathKey, path);
  localStorage.setItem(durableReturnPathKey, JSON.stringify({ path, expiresAt: Date.now() + returnPathLifetimeMs }));
  return path;
};

const consumeReturnPath = () => {
  const sessionPath = sessionStorage.getItem(returnPathKey);
  let durablePath = null;
  try {
    const saved = JSON.parse(localStorage.getItem(durableReturnPathKey) || "null");
    if (saved?.expiresAt > Date.now()) durablePath = saved.path;
  } catch {
    durablePath = null;
  }
  sessionStorage.removeItem(returnPathKey);
  localStorage.removeItem(durableReturnPathKey);
  return safeReturnPath(sessionPath || durablePath || "/");
};

const activeAccount = (app) => app.getActiveAccount() || app.getAllAccounts()[0] || null;

const createLogoutRequest = async (app) => {
  const account = activeAccount(app);
  const request = {
    account: account || undefined,
    postLogoutRedirectUri: globalThis.location?.origin
  };

  if (!account) return request;

  // Passing the account lets MSAL derive logout_hint from the optional
  // login_hint claim. Supplying the cached ID token also identifies the
  // exact Entra session without prompting the user during normal sign-out.
  try {
    const result = await app.acquireTokenSilent({ account, scopes: [apiScope] });
    if (result.idToken) request.idTokenHint = result.idToken;
  } catch {
    // Logout must still work if the token cache has expired. The account hint
    // remains useful, and MSAL will clear the local account before redirecting.
  }

  return request;
};

const getApplication = async () => {
  if (!configured) return null;
  if (!application) {
    application = new PublicClientApplication({
      auth: {
        clientId,
        authority: `https://${tenantName}.ciamlogin.com/${tenantId}`,
        redirectUri: globalThis.location?.origin,
        postLogoutRedirectUri: globalThis.location?.origin
      },
      cache: { cacheLocation: "localStorage" }
    });
    initialization = application.initialize().then(async () => {
      const result = await application.handleRedirectPromise();
      if (result?.account) application.setActiveAccount(result.account);
      const returnPath = consumeReturnPath();
      if (returnPath !== "/" && globalThis.location?.pathname === "/") {
        globalThis.history.replaceState({}, "", returnPath);
      }
    });
  }
  await initialization;
  return application;
};

export const auth = {
  configured,
  async currentAccount() {
    const app = await getApplication();
    if (!app) return { name: "Demo Player", username: "demo@ocheleague.com", demo: true };
    return activeAccount(app);
  },
  async signIn(returnPath = "/", email = "") {
    const app = await getApplication();
    const path = rememberReturnPath(returnPath);
    if (!app) return;
    await app.loginRedirect({
      scopes: ["openid", "email", apiScope],
      redirectStartPage: new URL(path, globalThis.location.origin).href,
      ...(path !== "/" ? invitationAuthOptions("signIn", email) : {})
    });
  },
  async signUp(returnPath = "/") {
    const app = await getApplication();
    const path = rememberReturnPath(returnPath);
    if (!app) return;
    await app.loginRedirect({
      scopes: ["openid", "email", apiScope],
      redirectStartPage: new URL(path, globalThis.location.origin).href,
      ...invitationAuthOptions("signUp")
    });
  },
  async signOut() {
    const app = await getApplication();
    if (app) await app.logoutRedirect(await createLogoutRequest(app));
  },
  async token() {
    const app = await getApplication();
    if (!app) return "";
    const account = activeAccount(app);
    if (!account) return "";
    try {
      return (await app.acquireTokenSilent({ account, scopes: [apiScope] })).accessToken;
    } catch {
      await app.acquireTokenRedirect({ account, scopes: [apiScope] });
      return "";
    }
  }
};
